import { yupResolver } from '@hookform/resolvers/yup';
import {
  useEditOtherRatesMutation,
  useGetOtherRatesQuery,
} from 'common/api/endpoints/harvestData';
import { handleError } from 'common/api/utils/handleError';
import { DataTable, DataTableProps } from 'common/components/DataTable';
import { mapOtherRates } from 'common/models/settings/otherRates';
import * as notifier from 'common/services/notification';
import {
  OtherRateRow,
  useOtherRatesTableData,
} from 'features/settings-view/hooks/useOtherRatesTableData';
import { FC, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

export type OtherRateValues = Record<string, number | null>;

export const OtherRates: FC = () => {
  const {
    data: otherRates,
    isLoading,
    isFetching,
    error,
  } = useGetOtherRatesQuery();
  const [editOtherRates, { isLoading: isSubmitting }] =
    useEditOtherRatesMutation();

  const { columns, data, isEditing, setIsEditing } = useOtherRatesTableData(
    otherRates || [],
    isSubmitting,
  );

  const otherRatesMap = useMemo(
    () => mapOtherRates(otherRates || []),
    [otherRates],
  );

  const defaultValues: OtherRateValues = useMemo(() => {
    return (
      otherRates?.reduce((acc, rate) => {
        acc[rate.id.toString()] = otherRatesMap.get(rate.type)?.rate || null;
        return acc;
      }, {} as OtherRateValues) || {}
    );
  }, [otherRates, otherRatesMap]);

  const methods = useForm<OtherRateValues>({
    values: defaultValues,
    resolver: yupResolver(yup.object({})),
    mode: 'all',
  });
  const {
    getFieldState,
    reset,
    formState: { isDirty },
  } = methods;

  const closeForm = () => {
    reset();
    setIsEditing(false);
  };

  const onSubmit = async (data: OtherRateValues): Promise<void> => {
    if (!isDirty) {
      closeForm();
      return;
    }

    const dto = Object.entries(data).reduce(
      (acc, [id, rate]) => {
        if (getFieldState(id).isDirty) {
          acc.push({
            id: parseInt(id, 10),
            rate: rate || 0,
          });
        }
        return acc;
      },
      [] as { id: number; rate: number }[],
    );

    try {
      await editOtherRates(dto).unwrap();
      notifier.showSuccessMessage('Rates updated successfully.');
      closeForm();
    } catch (err) {
      handleError(err, 'Failed to update rates.');
    }
  };

  const tableProps: DataTableProps<OtherRateRow, OtherRateValues> = {
    columns,
    data,
    isLoading,
    isFetching,
    maxTableWidth: '500px',
    formProps: {
      methods,
      onSubmit,
      onCancel: closeForm,
      isEditing,
      setIsEditing,
      isDirty,
      isSubmitting,
    },
  };

  useEffect(() => {
    if (error) {
      handleError(error, "Failed to fetch 'other' rates.");
    }
  }, [error]);

  return <DataTable {...tableProps} />;
};
